export const courseModules = [
  // ══════════════════════════════════════════════════════
  // MODULE 1 — TRAININGSLEER
  // ══════════════════════════════════════════════════════
  {
    id: 1,
    title: "Trainingsleer",
    subtitle: "De basisprincipes van hoe training werkt",
    color: "#C9A84C",
    lessons: [
      {
        id: 1,
        title: "Progressive overload",
        duration: "9 min",
        content: `
<h2>Progressive overload</h2>
<p>Stel je voor dat iemand voor het eerst een sportschool binnenloopt. Hij pakt een gewicht en doet een aantal herhalingen. Voor hem voelt dit zwaar, zijn spieren verzuren en na afloop voelt hij zich moe. Toch gebeurt er iets belangrijks dat je niet direct ziet.</p>

<h3>Homeostase: het evenwicht van het lichaam</h3>
<p>Het lichaam is van nature gericht op overleven en efficiëntie. Het probeert altijd energie te besparen en zo min mogelijk onnodige aanpassingen te maken. Dit evenwicht noemen we <strong>homeostase</strong>. Zolang het lichaam geen reden heeft om te veranderen, zal het dat ook niet doen.</p>

<h3>Overload: de noodzakelijke verstoring</h3>
<p>Wanneer iemand begint met trainen, doorbreekt hij deze balans. De training vormt een verstoring van de homeostase. Spieren raken vermoeid, energievoorraden nemen af en er ontstaat lichte schade in de spiervezels. Voor het lichaam is dit een signaal dat de huidige capaciteit niet voldoende was.</p>
<p>Hier ontstaat het principe van <strong>overload</strong>. Overload betekent dat het lichaam zwaarder belast wordt dan het gewend is. Dit is essentieel, omdat het lichaam alleen verandert wanneer het daartoe wordt gedwongen.</p>

<div class="lesson-example">
  <strong>Voorbeeld:</strong> Iemand die elke week tien push-ups doet, zal in het begin progressie maken. Maar na verloop van tijd worden deze tien push-ups makkelijk. Als er niets verandert, stopt de progressie. Pas wanneer hij vijftien push-ups doet of extra gewicht toevoegt, ontstaat er opnieuw een prikkel tot verandering.
</div>

<h3>Progressive overload in de praktijk</h3>
<p>Een schema kan nog zo goed in elkaar zitten, zonder progressie zal er geen resultaat zijn. Er zijn meerdere manieren om overload toe te passen:</p>
<ul>
  <li><strong>Gewicht verhogen</strong> — de meest voor de hand liggende methode</li>
  <li><strong>Meer herhalingen</strong> — met hetzelfde gewicht meer reps uitvoeren</li>
  <li><strong>Extra sets toevoegen</strong> — meer totaal volume</li>
  <li><strong>Rust verkorten</strong> — dezelfde belasting in minder tijd</li>
  <li><strong>Tempo aanpassen</strong> — langzamer uitvoeren voor meer spierspanning</li>
  <li><strong>Techniek verbeteren</strong> — efficiënter kracht uitoefenen</li>
</ul>

<h3>Progressie per niveau</h3>
<table class="lesson-table">
  <tr><th>Niveau</th><th>Snelheid</th><th>Aanpak</th></tr>
  <tr><td>Beginner</td><td>Wekelijks</td><td>Lineaire progressie (elke sessie iets meer)</td></tr>
  <tr><td>Intermediair</td><td>Wekelijks/maandelijks</td><td>Golvende progressie</td></tr>
  <tr><td>Gevorderd</td><td>Maandelijks/kwartaal</td><td>Periodisering in blokken</td></tr>
</table>

<h3>Wat gebeurt er zonder overload?</h3>
<p>Het lichaam raakt gewend aan de belasting en stopt met adapteren. Het schema blijft hetzelfde, maar resultaat blijft uit. <strong>Progressie is de motor achter elke vorm van ontwikkeling.</strong></p>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Progressive overload = het systematisch verhogen van de belasting om het lichaam te dwingen zich te blijven aanpassen.
</div>
        `
      },
      {
        id: 2,
        title: "Supercompensatie",
        duration: "8 min",
        content: `
<h2>Supercompensatie</h2>
<p>Om beter te begrijpen wat er na een training gebeurt, is het belangrijk om naar het verloop van prestatie te kijken.</p>

<h3>Wat is adaptatie?</h3>
<p>Tijdens training ontstaat er schade in de spiervezels. Deze schade is klein en gecontroleerd, maar noodzakelijk. Het lichaam herkent deze schade en start een herstelproces. Tijdens dit herstel wordt de spier niet alleen gerepareerd, maar ook iets <strong>sterker gemaakt dan voorheen</strong>. Dit proces noemen we <strong>adaptatie</strong>.</p>

<h3>Het verloop na een training</h3>
<p>Na een training is het lichaam tijdelijk verzwakt. Spieren zijn vermoeid, energie is verbruikt en het lichaam heeft tijd nodig om te herstellen. In deze fase is de prestatie <em>lager</em> dan voor de training.</p>
<p>Wanneer het lichaam voldoende tijd krijgt om te herstellen, keert het niet alleen terug naar het oude niveau, maar gaat het daar <strong>iets overheen</strong>. Dit noemen we <strong>supercompensatie</strong>.</p>

<img src="/images/anatomie/supercompensatie_curve.png" alt="Supercompensatiecurve" style="width:100%;border-radius:8px;margin:14px 0;" />

<h3>Timing is cruciaal</h3>
<p>De timing van een volgende training speelt een cruciale rol:</p>
<ul>
  <li><strong>Te vroeg trainen:</strong> Het lichaam bevindt zich nog in de herstelfase. Vermoeidheid stapelt zich op → risico op overtraining.</li>
  <li><strong>Op het juiste moment:</strong> De supercompensatie heeft plaatsgevonden → optimaal moment voor progressie.</li>
  <li><strong>Te laat trainen:</strong> Het lichaam keert terug naar het oorspronkelijke niveau → opgebouwde vooruitgang gaat deels verloren.</li>
</ul>

<div class="lesson-example">
  <strong>Voorbeeld:</strong> Iemand die na een zware beentraining drie dagen nodig heeft om volledig te herstellen. Als hij de volgende dag opnieuw zwaar traint, zal zijn prestatie lager zijn. Na drie dagen zit hij op het juiste moment. Wacht hij een week, dan is een deel van de adaptatie alweer verdwenen.
</div>

<h3>Wanneer groeit de spier?</h3>
<p>Een belangrijk misverstand is dat spiergroei plaatsvindt tijdens de training zelf. In werkelijkheid is het tegenovergestelde waar. Tijdens training <em>breek je het lichaam af</em>. De opbouw — en de supercompensatie — gebeurt pas daarna, tijdens rust en herstel.</p>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Supercompensatie = na herstel stijgt het prestatieniveau tijdelijk boven het beginpunt uit. Train op dit moment voor maximale progressie.
</div>
        `
      },
      {
        id: 3,
        title: "Herstel",
        duration: "8 min",
        content: `
<h2>Herstel</h2>
<p>Rust wordt vaak onderschat, terwijl het een essentieel onderdeel is van training. Zonder voldoende herstel kan het lichaam de prikkel van training niet verwerken en vindt er geen adaptatie plaats.</p>

<h3>De drie pijlers van resultaat</h3>
<p>Resultaat wordt bepaald door de combinatie van:</p>
<ul>
  <li><strong>Trainingsprikkel</strong> — voldoende overload</li>
  <li><strong>Herstel</strong> — slaap, voeding, rust</li>
  <li><strong>Consistentie</strong> — regelmatig herhalen over tijd</li>
</ul>
<p>Iemand die vier keer per week zwaar traint, maar slecht slaapt en weinig eet, zal moeite hebben om te herstellen. Hierdoor blijft de adaptatie achter en kan progressie uitblijven.</p>

<h3>Twee vormen van rust</h3>
<div class="lesson-system">
  <h4>1. Rust tussen sets</h4>
  <p>Bij korte rust blijft de vermoeidheid hoog, wat de intensiteit van de volgende set verlaagt. Bij langere rust kan iemand meer kracht leveren.</p>
  <ul>
    <li>Kracht (zware sets): 3–5 minuten rust</li>
    <li>Hypertrofie: 60–120 seconden rust</li>
    <li>Uithoudingsvermogen: 30–60 seconden rust</li>
  </ul>
</div>
<div class="lesson-system">
  <h4>2. Rust tussen trainingen</h4>
  <p>Bepaalt of het lichaam voldoende tijd heeft om volledig te herstellen en sterker terug te komen (supercompensatie).</p>
</div>

<h3>Factoren die herstel beïnvloeden</h3>
<ul>
  <li><strong>Slaap:</strong> 7–9 uur per nacht is voor de meeste sporters optimaal. Tijdens diepe slaap worden groeihormonen aangemaakt.</li>
  <li><strong>Voeding:</strong> Eiwitten voor spierherstel, koolhydraten voor energieaanvulling, voldoende calorieën.</li>
  <li><strong>Stress:</strong> Mentale en emotionele stress verhoogt cortisol, wat herstel vertraagt.</li>
  <li><strong>Hydratatie:</strong> Uitdroging vertraagt herstelprocessen en verslechtert prestaties.</li>
</ul>

<h3>Gevolgen van te weinig of te veel rust</h3>
<ul>
  <li><strong>Te weinig rust:</strong> Stapeling van vermoeidheid, verslechterde prestaties, verhoogd blessurerisico, stagnatie.</li>
  <li><strong>Te veel rust:</strong> Trainingsprikkel gaat verloren, progressie wordt minder efficiënt.</li>
</ul>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Herstel is geen verloren tijd — het is wanneer adaptatie plaatsvindt. Training en herstel zijn twee kanten van dezelfde medaille.
</div>
        `
      },
      {
        id: 4,
        title: "Vermoeidheid",
        duration: "8 min",
        content: `
<h2>Vermoeidheid</h2>
<p>Niet iedereen reageert hetzelfde op training. Wat voor de ene persoon een goede training is, kan voor een ander te zwaar of te licht zijn. Het begrijpen van vermoeidheid is essentieel om dit goed in te schatten.</p>

<h3>Belasting versus belastbaarheid</h3>
<ul>
  <li><strong>Belasting:</strong> Datgene wat je van het lichaam vraagt — het gewicht, het aantal herhalingen, de intensiteit.</li>
  <li><strong>Belastbaarheid:</strong> Wat het lichaam daadwerkelijk aankan, rekening houdend met ervaring, slaap, stress en gezondheid.</li>
</ul>
<p>Een goed trainingsprogramma zorgt ervoor dat de belasting <em>net boven</em> de huidige belastbaarheid ligt. Dit creëert een prikkel tot groei zonder het lichaam te overbelasten.</p>

<img src="/images/anatomie/belasting_belastbaarheid.png" alt="Belasting versus belastbaarheid" style="width:100%;border-radius:8px;margin:14px 0;" />

<h3>Soorten vermoeidheid</h3>
<div class="lesson-system">
  <h4>Acute vermoeidheid</h4>
  <p>Kortdurende vermoeidheid tijdens of direct na een training. Dit is normaal en gewenst — het is het signaal dat de prikkel sterk genoeg was.</p>
</div>
<div class="lesson-system">
  <h4>DOMS (Delayed Onset Muscle Soreness)</h4>
  <p>Vertraagde spierpijn die 24–72 uur na training optreedt. Wordt veroorzaakt door kleine beschadigingen in spiervezels. DOMS is een teken van een nieuwe of intensieve prikkel — niet perse van een effectieve training.</p>
</div>
<div class="lesson-system">
  <h4>Chronische vermoeidheid / overtraining</h4>
  <p>Langdurige stapeling van vermoeidheid door onvoldoende herstel. Kenmerken: aanhoudend slechtere prestaties, verminderde motivatie, verstoorde slaap, verhoogde rusthartslag en blessures.</p>
</div>

<h3>Individuele belastbaarheid</h3>
<p>Belastbaarheid is afhankelijk van meerdere factoren:</p>
<ul>
  <li>Trainingsgeschiedenis en ervaring</li>
  <li>Slaapkwaliteit en -duur</li>
  <li>Voedingsstatus</li>
  <li>Werkgerelateerde en mentale stress</li>
  <li>Leeftijd en herstelcapaciteit</li>
  <li>Genetische aanleg</li>
</ul>

<div class="lesson-example">
  <strong>Waarom is training individueel?</strong> Twee mensen kunnen hetzelfde schema volgen en totaal verschillend reageren. Een beginner heeft een lage belastbaarheid: zijn lichaam is nog niet gewend aan training, zijn techniek is minder ontwikkeld en zijn herstelcapaciteit is beperkt. Een ervaren sporter heeft een hogere belastbaarheid en heeft een zwaardere prikkel nodig.
</div>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Vermoeidheid is informatie — het geeft aan of de balans tussen belasting en belastbaarheid klopt. Een trainer stemt training af op wat de persoon <em>nu</em> aankan.
</div>
        `
      }
    ],
    quiz: [
      {
        question: "Wat beschrijft 'homeostase' in de context van training?",
        options: ["Een type periodiseringsschema", "Het herstelproces na een blessure", "Het evenwicht dat het lichaam probeert te bewaren", "Een trainingsmethode voor krachtsporters"],
        correct: 2
      },
      {
        question: "Wat is 'overload' in trainingswetenschappelijke zin?",
        options: ["Te veel trainen zodat je lichaam kapotgaat", "Altijd hetzelfde gewicht en herhalingen gebruiken", "Het lichaam zwaarder belasten dan het gewend is", "Meerdere spiergroepen tegelijk trainen in één sessie"],
        correct: 2
      },
      {
        question: "Wat beschrijft het principe van supercompensatie?",
        options: ["Na herstel stijgt het prestatieniveau tijdelijk boven het beginpunt", "Overtraining door te veel rust te nemen", "Het lichaam daalt terug naar basislijn na herstel", "Een geavanceerde strategie voor powerlifters"],
        correct: 0
      },
      {
        question: "Wanneer vindt spiergroei (adaptatie) werkelijk plaats?",
        options: ["Tijdens rust en herstel", "Meteen na de laatste set", "Tijdens de training zelf", "Uitsluitend tijdens de REM-slaap"],
        correct: 0
      },
      {
        question: "Wat kan er gebeuren als iemand te snel opnieuw traint vóór volledig herstel?",
        options: ["Sterkere adaptatie en snellere progressie", "Overtraining en opeenstapeling van vermoeidheid", "Meer energieopbouw in de spiercellen", "Hogere spierspanning per sessie"],
        correct: 1
      },
      {
        question: "Wat is DOMS?",
        options: ["Een trainingsmethode voor gevorderden", "Vertraagde spierpijn die 24–72 uur na training ontstaat", "Een type krachttraining op hoge intensiteit", "De maximale herstelperiode na overtraining"],
        correct: 1
      },
      {
        question: "Wat zijn de drie pijlers die het trainingsresultaat bepalen?",
        options: ["Frequentie, volume en intensiteit", "Trainingsprikkel, herstel en consistentie", "Voeding, slaap en genetica", "Gewicht, herhalingen en sets"],
        correct: 1
      },
      {
        question: "Welke rusttijd is aanbevolen tussen sets bij maximale krachtontwikkeling?",
        options: ["15–30 seconden", "60–90 seconden", "3–5 minuten", "10 minuten of meer"],
        correct: 2
      },
      {
        question: "Welke factor beïnvloedt de individuele belastbaarheid NIET direct?",
        options: ["Slaapkwaliteit en -duur", "Trainingsgeschiedenis en ervaring", "De kleur van de sportkleding", "Stress op het werk"],
        correct: 2
      },
      {
        type: "casus",
        casus: "Maik (24) traint al 2 maanden zes keer per week. Hij werkt hard in de sportschool, slaapt gemiddeld 5 uur per nacht en eet weinig. Toch ziet hij nauwelijks resultaat en voelt hij zich vaak moe en futloos.",
        question: "Wat is de meest waarschijnlijke verklaring voor het uitblijven van resultaat bij Maik?",
        options: ["Hij traint te weinig oefeningen per sessie", "Zijn schema is niet gevarieerd genoeg", "Te hoge trainingsfrequentie gecombineerd met onvoldoende herstel", "Hij heeft het verkeerde energiesysteem geactiveerd"],
        correct: 2
      },
      { type: "image_mc", image: "/images/anatomie/supercompensatie_curve.png", question: "Op welk punt in de supercompensatiecurve is het meest optimale moment om opnieuw te trainen?", options: ["Direct na de training, tijdens de herstelfase", "Zodra het lichaam hersteld is op de beginlijn", "Op het hoogtepunt van de supercompensatie", "Weken na de training wanneer het lichaam volledig is teruggekeerd"], correct: 2 },
      { type: "image_mc", image: "/images/anatomie/belasting_belastbaarheid.png", question: "Wat beschrijft de grafiek wanneer de belasting structureel de belastbaarheid overschrijdt?", options: ["Optimale trainingsprikkel voor maximale adaptatie", "Risico op overbelasting, vermoeidheid en blessures", "Supercompensatie treedt versneld op", "Belastbaarheid stijgt automatisch mee"], correct: 1 },
      { question: "Wat beschrijft het SAID-principe (Specific Adaptation to Imposed Demands)?", options: ["Het lichaam past zich aan door enkel rust na intensieve blokken", "Het lichaam adapteert specifiek aan de aard van de ontvangen trainingsprikkel", "Spiermassa groeit uitsluitend bij hoge herhalingsaantallen", "Herstel vindt alleen plaats tijdens diepe slaap"], correct: 1 },
      { question: "Welk energiesysteem is primair actief bij een maximale set van 1–3 herhalingen?", options: ["Aeroob oxidatief systeem", "Lactisch anaeroob systeem", "ATP-PCr systeem (fosfageen)", "Vetoxidatie"], correct: 2 },
      { question: "Wat is het doel van een deload-week in een trainingsprogramma?", options: ["Maximaal gewicht testen via 1RM-lifts", "Opgestapelde vermoeidheid reduceren en herstel bevorderen", "Nieuwe oefeningen introduceren met hoog volume", "Een volledige rustweek zonder enige training"], correct: 1 },
      { question: "Welke factor belemmert herstel het meest door verhoogde cortisolproductie?", options: ["Voldoende eiwitinname direct na de training", "Twee keer per week full body trainen", "Chronisch slaaptekort gecombineerd met hoge mentale stress", "Langzame excentrische oefentempo's"], correct: 2 },
      { question: "Wat is het verschil tussen acute en chronische vermoeidheid?", options: ["Acute vermoeidheid duurt weken; chronische vermoeidheid duurt uren", "Acute vermoeidheid treedt op direct na een training en herstelt snel; chronische vermoeidheid stapelt op door onvoldoende herstel over langere tijd", "Chronische vermoeidheid is minder gevaarlijk dan acute vermoeidheid", "Er is geen relevant onderscheid in trainingscontext"], correct: 1 },
      { question: "Een sporter traint maanden op dezelfde intensiteit zonder aanpassing. Welk trainingsprincipe wordt het meest geschonden?", options: ["Herstelprincipe", "Specificiteitsbeginsel", "Progressief overloadprincipe", "Reversibiliteitsprincipe"], correct: 2 }
    ]
  },

  // ══════════════════════════════════════════════════════
  // MODULE 2 — ANATOMIE
  // ══════════════════════════════════════════════════════
  {
    id: 2,
    title: "Anatomie",
    subtitle: "Het lichaam begrijpen als basis voor training",
    color: "#B8973A",
    lessons: [
      {
        id: 5,
        title: "Bewegingspatronen",
        duration: "8 min",
        content: `
<h2>Bewegingspatronen</h2>
<p>Het lichaam beweegt niet in losse spieren, maar in patronen. Wanneer je een oefening uitvoert, werken altijd meerdere spiergroepen en gewrichten samen om de beweging mogelijk te maken. Dit noemen we <strong>bewegingspatronen</strong>.</p>

<h3>Waarom zijn bewegingspatronen belangrijk?</h3>
<p>Een goed trainingsschema is niet gebaseerd op losse spieren, maar op het dekken van alle fundamentele bewegingen. Als een patroon structureel ontbreekt, ontstaan er <strong>musculaire disbalansen</strong> — ongelijke krachtverhoudingen die op lange termijn blessures kunnen veroorzaken.</p>

<h3>De zes fundamentele bewegingspatronen</h3>
<div class="lesson-system">
  <h4>1. Duwen — horizontaal</h4>
  <p>De armen bewegen horizontaal weg van het lichaam. Belast de borst, schouders en triceps.</p>
  <p><em>Voorbeelden:</em> Bench press, push-up, dumbbell press</p>
</div>
<div class="lesson-system">
  <h4>2. Duwen — verticaal</h4>
  <p>De armen bewegen omhoog boven het hoofd. Belast de schouders, triceps en bovenrug.</p>
  <p><em>Voorbeelden:</em> Overhead press, Arnold press, dumbbell shoulder press</p>
</div>
<div class="lesson-system">
  <h4>3. Trekken — horizontaal</h4>
  <p>De armen bewegen horizontaal naar het lichaam toe. Belast de midden- en bovenrug, biceps.</p>
  <p><em>Voorbeelden:</em> Barbell row, dumbbell row, seated cable row</p>
</div>
<div class="lesson-system">
  <h4>4. Trekken — verticaal</h4>
  <p>De armen bewegen omlaag van boven het hoofd. Belast de latissimus dorsi, biceps en bovenrug.</p>
  <p><em>Voorbeelden:</em> Pull-up, lat pulldown, assisted pull-up</p>
</div>
<div class="lesson-system">
  <h4>5. Squat (knie-dominant)</h4>
  <p>De knieën buigen en het heupgewricht daalt. Belast de quadriceps, billen en hamstrings.</p>
  <p><em>Voorbeelden:</em> Back squat, front squat, goblet squat, lunges</p>
</div>
<div class="lesson-system">
  <h4>6. Hip hinge (heup-dominant)</h4>
  <p>De heupen bewegen achterwaarts terwijl de rug recht blijft. Belast de hamstrings, billen en onderrug.</p>
  <p><em>Voorbeelden:</em> Deadlift, Romanian deadlift, hip thrust, kettlebell swing</p>
</div>

<h3>Balans in het schema</h3>
<p>In een goed schema komen alle zes patronen terug. Een veelgemaakte fout is het verwaarlozen van de trekbewegingen ten opzichte van de duwbewegingen. Iemand die structureel meer duwt dan trekt, kan schouderklachten ontwikkelen door een te dominante borstspier en te zwakke rugspieren.</p>

<img src="/images/anatomie/bewegingspatronen.png" alt="De 6 fundamentele bewegingspatronen" style="width:100%;border-radius:8px;margin:14px 0;" />

<div class="lesson-example">
  <strong>Praktische richtlijn:</strong> Streef naar een verhouding van minimaal 1:1 tussen duw- en trekbewegingen. Veel ervaren trainers hanteren zelfs 1:2 (voor elke duwbeweging twee trekbewegingen) voor optimale schoudergezondheid.
</div>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Traineer op basis van bewegingspatronen, niet op basis van losse spieren — zo ontstaat een gebalanceerd en effectief schema.
</div>
        `
      },
      {
        id: 6,
        title: "Spiergroepen",
        duration: "10 min",
        content: `
<h2>Spiergroepen</h2>
<p>Een trainer die weet welke spieren waarvoor dienen en hoe ze samenwerken, kan beter oefeningen selecteren, disbalansen herkennen en klanten uitleggen wat er tijdens een training gebeurt.</p>

<div class="lesson-anatomy-grid">
  <div class="lesson-anatomy-item">
    <img src="/images/anatomie/spieren_voor.png" alt="Spieren vooraanzicht" style="width:100%;border-radius:8px;" />
    <p style="text-align:center;font-size:0.85em;margin-top:6px;color:#C9A84C;font-weight:600;">Vooraanzicht</p>
  </div>
  <div class="lesson-anatomy-item">
    <img src="/images/anatomie/spieren_achter.png" alt="Spieren achteraanzicht" style="width:100%;border-radius:8px;" />
    <p style="text-align:center;font-size:0.85em;margin-top:6px;color:#C9A84C;font-weight:600;">Achteraanzicht</p>
  </div>
</div>

<h3>Bovenlichaam</h3>
<div class="lesson-system">
  <h4>Borstspieren (pectoralis major en minor)</h4>
  <p>De grote borstspier is verantwoordelijk voor het bewegen van de arm naar voren en naar binnen (adductie en flexie van de schouder). Centraal bij horizontale duwbewegingen zoals de bench press en push-up.</p>
</div>
<div class="lesson-system">
  <h4>Rugspieren</h4>
  <p><strong>Latissimus dorsi:</strong> De brede rugspier. Verantwoordelijk voor neerhalen en naar achteren trekken van de arm. Centraal bij verticale trekbewegingen (pull-up, lat pulldown).</p>
  <p><strong>Trapezius:</strong> Loopt van de nek tot het midden van de rug. Betrokken bij optrekken van de schouders en stabiliseren van het schouderblad.</p>
  <p><strong>Rhomboids:</strong> Liggen tussen de schouderbladen. Trekken de schouderbladen naar elkaar toe. Essentieel voor een goede houding.</p>
</div>
<div class="lesson-system">
  <h4>Schouders (deltoid)</h4>
  <p>Bestaat uit drie koppen: voorzijde (anterieur), zijkant (lateraal) en achterzijde (posterieur). Elke kop heeft een andere functie: duwen, zijwaarts heffen en trekken. Evenwichtige schoudertraining vereist aandacht voor alle drie koppen.</p>
</div>
<div class="lesson-system">
  <h4>Armen</h4>
  <p><strong>Biceps:</strong> Buigt de elleboog en supineert de onderarm. Betrokken bij alle trekbewegingen.</p>
  <p><strong>Triceps:</strong> Strekt de elleboog. Betrokken bij alle duwbewegingen. Vormt het grootste deel van de bovenarm.</p>
</div>

<img src="/images/anatomie/spieren_bovenlichaam.png" alt="Bovenlichaam spieren detail" style="width:100%;border-radius:8px;margin:12px 0;" />

<h3>Romp (core)</h3>
<p>De romp omvat alle spieren rondom de wervelkolom en het bekken. Deze spieren zijn niet primair bedoeld voor beweging, maar voor <strong>stabilisatie</strong>. Een sterke core beschermt de wervelkolom tijdens elke beweging.</p>
<ul>
  <li><strong>Rectus abdominis:</strong> De "buikspieren" (six-pack). Buigt de romp.</li>
  <li><strong>Obliques:</strong> Rotatie en laterale buiging van de romp.</li>
  <li><strong>Transversus abdominis:</strong> Diepste buikspier. Stabiliseert de wervelkolom als een korset.</li>
  <li><strong>Erector spinae:</strong> Strekken de wervelkolom. Houden de rug recht bij het tillen.</li>
</ul>

<h3>Onderlichaam</h3>
<div class="lesson-system">
  <h4>Quadriceps (voorkant bovenbeen)</h4>
  <p>Bestaat uit vier spieren. Primaire functie: strekken van de knie. Dominant bij squat-patronen en trappen lopen. Grootste spiergroep van het lichaam.</p>
</div>
<div class="lesson-system">
  <h4>Hamstrings (achterkant bovenbeen)</h4>
  <p>Drie spieren die de knie buigen en de heup strekken. Antagonist van de quadriceps. Dominant bij hip hinge bewegingen (deadlift, Romanian deadlift). Vaak zwakker dan de quad — een belangrijke disbalans om te corrigeren.</p>
</div>
<div class="lesson-system">
  <h4>Bilspieren (gluteus)</h4>
  <p>De gluteus maximus is de krachtigste spier van het lichaam. Strekt de heup. Cruciaal voor deadlifts, hip thrusts en squats. De gluteus medius en minimus stabiliseren het bekken bij enkelbeen-bewegingen.</p>
</div>
<div class="lesson-system">
  <h4>Kuiten (gastrocnemius en soleus)</h4>
  <p>Buigen de enkel (plantairflexie). Betrokken bij lopen, springen en staan. Worden vaak ondergetraind in schema's.</p>
</div>

<img src="/images/anatomie/spieren_onderlichaam.png" alt="Core en onderlichaam spieren" style="width:100%;border-radius:8px;margin:12px 0;" />

<h3>Agonist en antagonist</h3>
<p>Spieren werken in paren. De <strong>agonist</strong> is de primaire spier die de beweging uitvoert; de <strong>antagonist</strong> remt de beweging af en zorgt voor controle en balans. Voorbeelden: biceps (agonist) — triceps (antagonist) bij elleboogbuiging; quadriceps (agonist) — hamstrings (antagonist) bij kniestrekking.</p>
<p>Een disbalans tussen agonist en antagonist vergroot het blessurerisico. Een schema dat alleen de agonisten traint, is onvolledig.</p>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Kennis van spiergroepen helpt een trainer om gerichte keuzes te maken in oefeningsselectie en disbalansen vroegtijdig te signaleren.
</div>
        `
      },
      {
        id: 7,
        title: "Gewrichten",
        duration: "8 min",
        content: `
<h2>Gewrichten</h2>
<p>Elk gewricht in het lichaam heeft een specifieke bouw, een eigen bewegingsvrijheid en een eigen kwetsbaarheid. Als trainer is het essentieel om te begrijpen hoe gewrichten werken — zodat je veilig traint, blessures voorkomt en bewegingskwaliteit kunt beoordelen.</p>

<h3>Soorten gewrichten</h3>
<div class="lesson-system">
  <h4>Kogelgewricht</h4>
  <p>Heeft bewegingsvrijheid in meerdere richtingen. Kan buigen, strekken, draaien en zijwaarts bewegen.</p>
  <p><em>Voorbeelden:</em> Schouder, heup</p>
</div>
<div class="lesson-system">
  <h4>Scharniergewricht</h4>
  <p>Beweegt in slechts één vlak: buigen en strekken. Stabiel, maar beperkte vrijheid.</p>
  <p><em>Voorbeelden:</em> Elleboog, knie, enkels (deels)</p>
</div>

<h3>De belangrijkste gewrichten in training</h3>
<table class="lesson-table">
  <tr><th>Gewricht</th><th>Type</th><th>Relevante oefeningen</th></tr>
  <tr><td>Schouder</td><td>Kogelgewricht</td><td>Bench press, overhead press, pull-up</td></tr>
  <tr><td>Elleboog</td><td>Scharniergewricht</td><td>Curls, triceps extensions, rows</td></tr>
  <tr><td>Heup</td><td>Kogelgewricht</td><td>Deadlift, squat, hip thrust</td></tr>
  <tr><td>Knie</td><td>Scharniergewricht</td><td>Squat, lunges, leg press</td></tr>
  <tr><td>Enkel</td><td>Scharnier (deels kogelgewricht)</td><td>Calf raises, squat (mobiliteit)</td></tr>
  <tr><td>Wervelkolom</td><td>Combinatie van kleine gewrichten</td><td>Alle oefeningen in belasting</td></tr>
</table>

<img src="/images/anatomie/gewrichten_anatomie.png" alt="Anatomie van de gewrichten" style="width:100%;border-radius:8px;margin:14px 0;" />

<h3>Mobiliteit versus stabiliteit</h3>
<p>Niet elk gewricht heeft dezelfde behoefte. Het concept <strong>mobiliteit vs. stabiliteit</strong> beschrijft welke gewrichten primair bewegingsvrijheid nodig hebben en welke primair stabiel moeten zijn:</p>
<ul>
  <li><strong>Enkel:</strong> Mobiliteit — voldoende dorsaalflexie nodig voor diep squatten</li>
  <li><strong>Knie:</strong> Stabiliteit — een instabiele knie leidt snel tot blessures</li>
  <li><strong>Heup:</strong> Mobiliteit — beperkte heupflexibiliteit veroorzaakt compensaties in de rug</li>
  <li><strong>Lage rug:</strong> Stabiliteit — niet bedoeld voor grote bewegingsuitslagen onder belasting</li>
  <li><strong>Schouder:</strong> Mobiliteit — heeft de meeste vrijheid van alle gewrichten</li>
</ul>

<h3>Range of Motion (ROM)</h3>
<p><strong>Range of Motion</strong> beschrijft de maximale bewegingsuitslag van een gewricht. Een beperkte ROM kan de effectiviteit van oefeningen verminderen en compensaties veroorzaken in andere gewrichten.</p>

<div class="lesson-example">
  <strong>Praktijkvoorbeeld:</strong> Een klant met beperkte enkelmobiliteit kan niet diep genoeg squatten zonder zijn hielen op te tillen of voorover te zakken. De lage rug compenseert dan wat de enkel niet kan. De oplossing ligt niet in meer squatten, maar in het verbeteren van de enkelmobiliteit.
</div>

<h3>Gewrichtsveiligheid bij training</h3>
<p>Elk gewricht heeft grenzen. Overbelasting, verkeerde uitvoering of bewegen buiten de functionele ROM verhoogt het blessurerisico. Algemene richtlijnen:</p>
<ul>
  <li>Knieen bewegen altijd in de richting van de tenen bij squats en lunges</li>
  <li>De lage rug blijft neutraal (niet volledig hol, niet gebogen) bij tilbewegingen</li>
  <li>Schouders worden niet gedwongen in extreme hoekmouwen bij drukbewegingen</li>
</ul>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Gewrichten hebben elk hun eigen bewegingsvrijheid en kwetsbaarheid. Een trainer die dit begrijpt, kan beter oefeningen aanpassen en blessures voorkomen.
</div>
        `
      }
    ],
    quiz: [
      {
        question: "Wat is een bewegingspatroon in de context van training?",
        options: ["De specifieke volgorde van oefeningen in een sessie", "Een fysiologische categorie van beweging waarbij meerdere spieren samenwerken", "Vaste schema's speciaal ontworpen voor beginners", "De snelheid waarmee een oefening wordt uitgevoerd"],
        correct: 1
      },
      {
        question: "Welk bewegingspatroon is dominant bij een deadlift?",
        options: ["Horizontaal duwen", "Squat (knie-dominant)", "Hip hinge (heup-dominant)", "Verticaal trekken"],
        correct: 2
      },
      {
        question: "Wat is het risico bij structureel meer duwbewegingen dan trekbewegingen trainen?",
        options: ["Snellere progressie in de borstspieren", "Musculaire disbalans en verhoogd risico op schouderklachten", "Betere houdingsstabiliteit door sterkere borstspieren", "Geen risico — duwbewegingen zijn veiliger dan trekbewegingen"],
        correct: 1
      },
      {
        question: "Wat is de functie van de latissimus dorsi?",
        options: ["Buigen van de knie bij squat-bewegingen", "Stabiliseren van het schouderblad bij duwbewegingen", "Neerhalen en naar achteren trekken van de arm", "Strekken van de elleboog bij drukbewegingen"],
        correct: 2
      },
      {
        question: "Welke spiergroep is de antagonist van de quadriceps?",
        options: ["Gluteus maximus", "Hamstrings", "Kuiten (gastrocnemius)", "Iliopsoas"],
        correct: 1
      },
      {
        question: "Wat beschrijft 'Range of Motion' (ROM)?",
        options: ["De totale trainingsbelasting per sessie", "De maximale bewegingsuitslag van een gewricht", "De aanbevolen rusttijd na een training", "Het percentage van de 1RM dat gebruikt wordt"],
        correct: 1
      },
      {
        question: "Welk type gewricht is het schoudergewricht?",
        options: ["Scharniergewricht met één bewegingsrichting", "Kogelgewricht met bewegingsvrijheid in meerdere richtingen", "Vlak gewricht voor glijdende bewegingen", "Ellipsgewricht voor buigen en strekken"],
        correct: 1
      },
      {
        question: "Welk gewricht heeft PRIMAIR stabiliteit nodig in plaats van mobiliteit?",
        options: ["Heupgewricht", "Schoudergewricht", "Kniegewricht", "Enkelgewricht"],
        correct: 2
      },
      {
        question: "Waarom is kennis van agonist-antagonist paren belangrijk voor een trainer?",
        options: ["Zodat de trainer dezelfde oefening in verschillende tempi kan uitleggen", "Om disbalansen te herkennen en blessures te voorkomen door gebalanceerde training", "Het is niet relevant — moderne schema's gebruiken geen agonist-antagonist indeling", "Alleen voor gevorderde sporters die isolatieoefeningen doen"],
        correct: 1
      },
      {
        type: "casus",
        casus: "Pieter (28) klaagt dat hij bij squatten zijn hielen niet op de grond kan houden en voorover zakt. Zijn kniepijn verergert na elke beentraining. Zijn schema bevat uitsluitend squat-variaties en leg press voor de benen.",
        question: "Wat is de meest waarschijnlijke oorzaak en welke aanpak is het meest passend?",
        options: ["Pieter squatst met te licht gewicht — meer gewicht zorgt voor betere stabiliteit", "Beperkte enkelmobiliteit veroorzaakt compensaties; zijn schema mist ook heupgerichte oefeningen (hip hinge). Aanpak: enkelmobiliteit verbeteren en hip hinge oefeningen toevoegen", "Pieter heeft knieklachten door een tekort aan calcium; voedingsadvies is de prioriteit", "Meer squatten is de oplossing — techniek verbetert vanzelf door herhaling"],
        correct: 1
      },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_biceps.png",       question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Triceps brachii", "Biceps brachii", "Deltoideus", "Brachioradialis"], correct: 1 },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_triceps.png",      question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Biceps brachii", "Deltoideus", "Triceps brachii", "Teres major"], correct: 2 },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_pectoralis.png",   question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Latissimus dorsi", "Deltoideus", "Serratus anterior", "Pectoralis major"], correct: 3 },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_latissimus.png",   question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Trapezius", "Latissimus dorsi", "Erector spinae", "Rhomboideus"], correct: 1 },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_deltoid.png",      question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Pectoralis major", "Trapezius", "Deltoideus", "Biceps brachii"], correct: 2 },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_rectus.png",       question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Obliquus abdominis", "Transversus abdominis", "Rectus abdominis", "Iliopsoas"], correct: 2 },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_quadriceps.png",   question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Biceps femoris", "Gluteus maximus", "Quadriceps femoris", "Gastrocnemius"], correct: 2 },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_hamstrings.png",   question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Quadriceps femoris", "Biceps femoris", "Gluteus maximus", "Gastrocnemius"], correct: 1 },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_gluteus.png",      question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Biceps femoris", "Erector spinae", "Gluteus maximus", "Quadriceps femoris"], correct: 2 },
      { type: "image_mc", image: "/images/anatomie/vragen/sq_gastrocnemius.png",question: "Welke spier wordt aangeduid op de afbeelding?", options: ["Soleus", "Biceps femoris", "Tibialis anterior", "Gastrocnemius"], correct: 3 },
      { type: "image_mc", image: "/images/anatomie/bewegingspatronen.png", question: "Welk bewegingspatroon op de afbeelding is dominant bij een pull-up of lat pulldown?", options: ["Horizontaal duwen", "Verticaal duwen", "Horizontaal trekken", "Verticaal trekken"], correct: 3 },
      { type: "image_mc", image: "/images/anatomie/spieren_bovenlichaam.png", question: "Welke grote rugspier op de afbeelding is primair verantwoordelijk voor het neerhalen van de arm?", options: ["Trapezius", "Rhomboideus", "Latissimus dorsi", "Deltoideus posterieur"], correct: 2 },
      { type: "image_mc", image: "/images/anatomie/gewrichten_anatomie.png", question: "Welk gewricht op de afbeelding heeft de grootste bewegingsvrijheid en is daarmee ook het kwetsbaarst bij verkeerde uitvoering?", options: ["Kniegewricht", "Ellebooggewricht", "Schoudergewricht", "Enkelgewricht"], correct: 2 },
      { question: "Wat is de primaire functie van de trapezius bij krachttraining?", options: ["Strekken van de elleboog bij drukbewegingen", "Optrekken van de schouders (elevatie) en stabilisatie van het schouderblad", "Buigen van de heup bij squat-bewegingen", "Rotatie van de wervelkolom"], correct: 1 },
      { question: "Welke spier is de directe antagonist van de biceps brachii?", options: ["Brachioradialis", "Deltoideus", "Triceps brachii", "Teres minor"], correct: 2 },
      { question: "Welke spiergroep is primair actief bij een lat pulldown?", options: ["Pectoralis major", "Trapezius", "Latissimus dorsi", "Deltoideus anterieur"], correct: 2 },
      { question: "Wat is de functie van de gluteus medius in sport en beweging?", options: ["Strekken van de heup bij deadlifts en hip thrusts", "Stabilisatie van het bekken bij lopen en eenbeen-bewegingen", "Plantairflexie van de enkel", "Buigen van de knie bij hamstring curls"], correct: 1 },
      { question: "Welke spiergroep vormt het grootste volume van de bovenarm?", options: ["Biceps brachii", "Brachialis", "Triceps brachii", "Coracobrachialis"], correct: 2 }
    ]
  },

  // ══════════════════════════════════════════════════════
  // MODULE 3 — PROGRAMMEREN
  // ══════════════════════════════════════════════════════
  {
    id: 3,
    title: "Programmeren",
    subtitle: "Trainingsschema's opbouwen in de praktijk",
    color: "#A08530",
    lessons: [
      {
        id: 8,
        title: "Oefeningselectie",
        duration: "9 min",
        content: `
<h2>Oefeningselectie</h2>
<p>Wanneer iemand begint met het maken van een trainingsschema, is de eerste neiging om direct oefeningen te kiezen. Toch ligt hier meteen een valkuil. Veel beginnende trainers kiezen oefeningen die ze kennen of leuk vinden, zonder te begrijpen waarom ze die kiezen.</p>

<h3>Compound vs. isolatie oefeningen</h3>
<div class="lesson-system">
  <h4>Compound oefeningen</h4>
  <p>Bewegingen waarbij meerdere gewrichten en spiergroepen tegelijk samenwerken. Deze oefeningen vragen veel van het lichaam, stimuleren een grotere hormonale respons en leveren meer functioneel volume per tijdseenheid.</p>
  <p><em>Voorbeelden:</em> Squat, deadlift, bench press, pull-up, overhead press, barbell row</p>
</div>
<div class="lesson-system">
  <h4>Isolatie oefeningen</h4>
  <p>Richten zich op één spiergroep en meestal één gewricht. Minder complex en gebruikt om specifieke spieren extra te belasten of zwakke schakels te versterken.</p>
  <p><em>Voorbeelden:</em> Bicep curl, leg extension, lateral raise, calf raise, triceps pushdown</p>
</div>

<h3>Selectiecriteria</h3>
<p>Goede oefeningskeuze is gebaseerd op:</p>
<ul>
  <li><strong>Het doel:</strong> Wat wil de klant bereiken? Kracht vraagt andere oefeningen dan hypertrofie.</li>
  <li><strong>Het niveau:</strong> Een beginner heeft eenvoudige, controleerbare bewegingen nodig. Een gevorderde kan complexere patronen aan.</li>
  <li><strong>Blessures en beperkingen:</strong> Welke oefeningen kan de klant veilig uitvoeren?</li>
  <li><strong>Beschikbare apparatuur:</strong> Niet elke sportschool heeft dezelfde middelen.</li>
  <li><strong>Bewegingspatroon balans:</strong> Dekt het schema alle zes bewegingspatronen?</li>
</ul>

<h3>Compound als basis, isolatie als aanvulling</h3>
<p>In de meeste schema's vormen compound oefeningen de kern. Isolatie-oefeningen worden daarna gebruikt om specifieke spiergroepen extra aandacht te geven. Dit principe geldt ongeacht het doel.</p>

<div class="lesson-example">
  <strong>Gevaar van disbalans:</strong> Iemand die alleen borst en armen traint maar zijn rug en benen vergeet, creëert musculaire disbalans die op lange termijn tot blessures kan leiden.
  <br/><br/>
  <strong>Alternatief bij blessures:</strong> Knieklachten bij squatten? Kies voor leg press, split squat, step-up of hip thrust — afhankelijk van wat de belastbaarheid toelaat.
</div>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Goede oefeningsselectie begint met het doel, het niveau en de beperkingen van de klant — en zorg dat alle bewegingspatronen aan bod komen.
</div>
        `
      },
      {
        id: 9,
        title: "Sets en herhalingen",
        duration: "9 min",
        content: `
<h2>Sets en herhalingen</h2>
<p>Wanneer het doel duidelijk is en de oefeningen gekozen zijn, komt de volgende stap: hoe zwaar en hoe vaak ga je iets uitvoeren?</p>

<h3>De kernvariabelen</h3>
<ul>
  <li><strong>Herhalingen (reps):</strong> Hoe vaak een beweging achter elkaar wordt uitgevoerd.</li>
  <li><strong>Sets:</strong> Hoe vaak je een reeks herhalingen doet.</li>
  <li><strong>Intensiteit:</strong> Hoe zwaar de belasting is ten opzichte van het maximum (1RM).</li>
</ul>

<h3>1RM en percentages</h3>
<p>Intensiteit wordt uitgedrukt als percentage van de <strong>1RM (één herhalingsmaximum)</strong> — het maximale gewicht dat iemand één keer kan verplaatsen. Trainen met 80% van de 1RM betekent dat het gewicht zwaar genoeg is voor slechts een beperkt aantal herhalingen.</p>

<h3>Herhalingszones en hun effect</h3>
<table class="lesson-table">
  <tr><th>Doel</th><th>Herhalingen</th><th>Intensiteit</th><th>Sets</th></tr>
  <tr><td>Kracht</td><td>1–5</td><td>85–100% 1RM</td><td>3–5</td></tr>
  <tr><td>Hypertrofie</td><td>6–12</td><td>65–85% 1RM</td><td>3–4</td></tr>
  <tr><td>Uithoudingsvermogen</td><td>15+</td><td>&lt;65% 1RM</td><td>2–3</td></tr>
</table>

<img src="/images/anatomie/sets_herhalingen_zones.png" alt="Herhalingszones en intensiteit" style="width:100%;border-radius:8px;margin:14px 0;" />

<h3>Time Under Tension (TUT)</h3>
<p><strong>Time under tension</strong> beschrijft hoe lang een spier onder spanning staat tijdens een set. Tempo beïnvloedt de TUT. Een langzamer tempo (bijv. 3 seconden zakken, 1 seconde pauze, 2 seconden omhoog) verhoogt de TUT en kan de prikkel voor spiergroei vergroten.</p>

<div class="lesson-example">
  <strong>Voorbeeld:</strong> Iemand die 3 sets van 5 herhalingen doet met een zwaar gewicht — hoge belasting op het zenuwstelsel, focus op kracht. Dezelfde persoon die 3 sets van 12 herhalingen doet met een lichter gewicht — meer spierspanning over tijd, grotere prikkel voor spiergroei.
</div>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Sets × herhalingen × intensiteit bepalen de trainingsprikkel en moeten altijd aansluiten op het doel van de klant.
</div>
        `
      },
      {
        id: 10,
        title: "Frequentie",
        duration: "8 min",
        content: `
<h2>Frequentie</h2>
<p>Naast de intensiteit en het volume speelt ook de frequentie een grote rol: hoe vaak train je, en hoe verdeel je het werk over de week?</p>

<h3>Trainingsvolume</h3>
<p><strong>Volume</strong> geeft aan hoeveel werk er in totaal wordt verricht. Het wordt berekend door sets × herhalingen × gewicht. Iemand die 5 sets van 10 herhalingen doet met een bepaald gewicht heeft meer volume dan iemand die 3 sets van 10 herhalingen doet.</p>
<p>Volume is een belangrijke factor voor spiergroei: meer volume leidt tot meer spierspanning en een grotere hypertrofieprikkel — mits het lichaam voldoende kan herstellen.</p>

<h3>Trainingsfrequentie</h3>
<p><strong>Frequentie</strong> verwijst naar hoe vaak een spiergroep per week wordt getraind. Twee keer per week dezelfde spiergroep trainen met lager volume per sessie kan effectiever zijn dan één keer per week met hoog volume.</p>

<div class="lesson-example">
  <strong>Voorbeeld:</strong> Iemand die zijn borst één keer per week met 20 sets traint versus iemand die dit verdeelt over 2 trainingen van 10 sets. In veel gevallen leidt de tweede aanpak tot betere prestaties per set en meer progressie — omdat het lichaam de eiwitaanmaak tweemaal per week kan activeren.
</div>

<h3>De balans</h3>
<p>Volume en frequentie moeten altijd in balans zijn met de belastbaarheid van het individu. Een beginnerssporter heeft minder volume nodig dan een gevorderde. Te veel volume of een te hoge frequentie → overbelasting. Te weinig → onvoldoende prikkel.</p>

<h3>Praktische richtlijnen</h3>
<table class="lesson-table">
  <tr><th>Niveau</th><th>Sets per spiergroep/week</th><th>Frequentie/week</th></tr>
  <tr><td>Beginner</td><td>10–15</td><td>2–3x</td></tr>
  <tr><td>Intermediair</td><td>12–20</td><td>2–4x</td></tr>
  <tr><td>Gevorderd</td><td>16–25+</td><td>3–6x</td></tr>
</table>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Volume = sets × reps × gewicht. Frequentie = hoe vaak per week. Beide moeten aansluiten op het niveau en de belastbaarheid van de klant.
</div>
        `
      },
      {
        id: 11,
        title: "Opbouw van trainingsschema's",
        duration: "10 min",
        content: `
<h2>Opbouw van trainingsschema's</h2>
<p>Een schema bestaat niet alleen uit losse oefeningen, maar uit een structuur over meerdere dagen en weken. Deze structuur bepaalt hoe spiergroepen worden getraind en hoe de belasting verdeeld wordt.</p>

<h3>Schema-types</h3>
<div class="lesson-system">
  <h4>Full body schema</h4>
  <p>Bij een full body schema worden in één training meerdere spiergroepen getraind. Iemand traint bijvoorbeeld 3x per week het hele lichaam.</p>
  <p><em>Geschikt voor:</em> Beginners — ze oefenen vaker dezelfde bewegingen en leren sneller. De totale belasting per training is beter beheersbaar.</p>
</div>
<div class="lesson-system">
  <h4>Upper/Lower split</h4>
  <p>Boven- en onderlichaam worden afwisselend getraind. Typisch 4 trainingsdagen per week.</p>
  <p><em>Geschikt voor:</em> Intermediaire sporters met 4 beschikbare trainingsdagen.</p>
</div>
<div class="lesson-system">
  <h4>Push/Pull/Legs (PPL)</h4>
  <p>Trainingen zijn verdeeld in duwbewegingen, trekbewegingen en beentraining. Doorgaans 6 trainingsdagen.</p>
  <p><em>Geschikt voor:</em> Gevorderde sporters die meer volume per spiergroep kunnen verwerken.</p>
</div>

<h3>Schema vergelijking</h3>
<table class="lesson-table">
  <tr><th>Type</th><th>Dagen/week</th><th>Volume/sessie</th><th>Best voor</th></tr>
  <tr><td>Full body</td><td>2–4x</td><td>Laag per spiergroep</td><td>Beginners</td></tr>
  <tr><td>Upper/Lower</td><td>4x</td><td>Matig per spiergroep</td><td>Intermediair</td></tr>
  <tr><td>Push/Pull/Legs</td><td>6x</td><td>Hoog per spiergroep</td><td>Gevorderd</td></tr>
</table>

<img src="/images/anatomie/schema_types.png" alt="Schema-types vergelijking" style="width:100%;border-radius:8px;margin:14px 0;" />

<h3>Het 5-stappenproces voor schema-opbouw</h3>
<div class="lesson-system">
  <h4>Stap 1: Bepaal het doel</h4>
  <p>Kracht, hypertrofie, vetverlies, uithoudingsvermogen? Zonder doel is er geen richting.</p>
</div>
<div class="lesson-system">
  <h4>Stap 2: Kies de trainingsvariabelen</h4>
  <p>Welk herhalingsbereik past bij het doel? Welke intensiteit? Hoeveel volume? Hoeveel rustperiodes?</p>
</div>
<div class="lesson-system">
  <h4>Stap 3: Selecteer oefeningen</h4>
  <p>Kies oefeningen die alle bewegingspatronen dekken. Compound als basis, isolatie als aanvulling.</p>
</div>
<div class="lesson-system">
  <h4>Stap 4: Bepaal de structuur</h4>
  <p>Full body of split? Hoeveel dagen per week? Hoe verdeel je volume over de week?</p>
</div>
<div class="lesson-system">
  <h4>Stap 5: Plan progressive overload</h4>
  <p>Hoe gaat de belasting in de komende weken toenemen? Plan dit van tevoren.</p>
</div>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Schema-opbouw = een systematisch proces van doel → variabelen → oefeningen → structuur → progressie. Elke stap bouwt voort op de vorige.
</div>
        `
      }
    ],
    quiz: [
      {
        question: "Wat is het kenmerkende verschil tussen een compound en een isolatie oefening?",
        options: ["Compound betrekt meerdere gewrichten en spiergroepen tegelijk", "Compound oefeningen zijn uitsluitend voor gevorderden", "Isolatie oefeningen zijn altijd zwaarder", "Er is geen wezenlijk verschil in effectiviteit"],
        correct: 0
      },
      {
        type: "open",
        question: "Welke herhalingsrange is primair gericht op krachtontwikkeling? (vul in, bijv. 6-12)",
        hint: "Noteer als: getal-getal",
        correctAnswer: "1-5"
      },
      {
        question: "Hoe wordt trainingsvolume berekend?",
        options: ["Trainingstijd gedeeld door rusttijd", "Gewicht maal duur van de training", "Alleen het totale aantal sets per week", "Sets × herhalingen × gewicht"],
        correct: 3
      },
      {
        type: "open",
        question: "Welke herhalingsrange past het best bij hypertrofie (spiergroei)? (vul in, bijv. 1-5)",
        hint: "Noteer als: getal-getal",
        correctAnswer: "6-12"
      },
      {
        question: "Wat is 'trainingsfrequentie'?",
        options: ["De snelheid waarmee een oefening wordt uitgevoerd", "Het totale aantal oefeningen per training", "Hoe zwaar de belasting is ten opzichte van de 1RM", "Hoe vaak een spiergroep per tijdseenheid wordt getraind"],
        correct: 3
      },
      {
        question: "Voor welk type sporter is een full body schema doorgaans het meest geschikt?",
        options: ["Gevorderde powerlifters", "Beginners", "Topsporters met dagelijkse training", "Sporters die 5+ dagen trainen"],
        correct: 1
      },
      {
        question: "Welke oefeningen worden het best als EERSTE uitgevoerd in een training?",
        options: ["De makkelijkste, voor een goede warming-up", "Compound oefeningen, terwijl energie en focus op hun hoogtepunt zijn", "Ze zijn korter en nemen minder tijd in beslag", "Ze vereisen minder technische vaardigheid"],
        correct: 1
      },
      {
        question: "Wat is het correct startpunt bij het opbouwen van een trainingsschema?",
        options: ["Oefeningen kiezen die je goed beheerst", "Trainingsfrequentie als eerste vaststellen", "Het trainingsdoel bepalen", "Beschikbare apparatuur als uitgangspunt nemen"],
        correct: 2
      },
      {
        question: "Wat is het gevolg van langdurig gebrek aan progressive overload?",
        options: ["Verhoogde motivatie door vertrouwde routine", "Continue verbetering door spiergeheugen", "Adaptatie stopt doordat het lichaam gewend raakt", "Verbeterde technische uitvoering bij zware belasting"],
        correct: 2
      },
      {
        type: "casus",
        casus: "David (32) wil 3x per week trainen voor spieropbouw. Hij heeft een kantoorbaan en heeft daarvoor nooit gesport. Hij beschikt over een volledige sportschool. Hij vraagt jou welk schema-type hij het beste kan volgen.",
        question: "Welk schema-type past het meest bij David's situatie?",
        options: ["Upper-lower split voor maximaal volume per spiergroep", "Full body schema omdat beginners profiteren van hoge frequentie per spiergroep", "Een 6-daags PPL-schema voor maximale spiergroei", "Isolatie-training per spiergroep voor sneller resultaat"],
        correct: 1
      },
      { question: "Een sporter traint 3 sets van 10 herhalingen op 75% van zijn 1RM. Op welk trainingsdoel is dit primair gericht?", options: ["Krachtontwikkeling (1–5 reps, 85–100% 1RM)", "Hypertrofie (6–12 reps, 65–85% 1RM)", "Uithoudingsvermogen (15+ reps, <65% 1RM)", "Dit valt buiten de standaard zones"], correct: 1 },
      { question: "Welk schema-type is het meest geschikt voor een intermediaire sporter met 4 trainingsdagen per week?", options: ["Full body schema (2–4x/week)", "Upper-Lower split (4x/week)", "Push-Pull-Legs (6x/week)", "Alle schema-types zijn even geschikt"], correct: 1 },
      { type: "open", question: "Welke herhalingsrange past primair bij uithoudingsvermogen? (vul in, bijv. 1-5)", hint: "Noteer als: 15+", correctAnswer: "15+" },
      { question: "Wat is RPE (Rate of Perceived Exertion) in trainingsprogrammering?", options: ["Repetitie Per Exercitie — totaal herhalingen per oefening", "Een subjectieve schaal (1-10) die de ervaren inspanningsintensiteit meet", "Resistance Per Exercise — het gebruikte gewicht", "Recovery Potential Estimate — herstelschatting na een training"], correct: 1 },
      { question: "Wat is het kenmerk van lineaire periodisering?", options: ["Intensiteit en volume variëren willekeurig per sessie", "Systematisch toenemende intensiteit met geleidelijk dalend volume over weken of maanden", "Afwisselen van zware en lichte trainingen binnen dezelfde week", "Elke week een compleet ander type training"], correct: 1 },
      { question: "Hoeveel sets per spiergroep per week wordt aanbevolen voor gevorderde sporters (hypertrofie)?", options: ["4–8 sets", "8–12 sets", "12–20 sets", "25–35 sets"], correct: 2 },
      { question: "Wanneer voeg je isolatie-oefeningen het meest zinvol toe aan een schema?", options: ["Als eerste oefening om de doelspier op te warmen", "Na compound oefeningen, om specifieke spiergroepen extra te belasten", "Uitsluitend bij beginners die compound oefeningen niet aankunnen", "Alleen in de vetverliefase voor meer definitie"], correct: 1 },
      { type: "casus", casus: "Lena (27) traint 4x/week Upper-Lower split en maakt al 3 weken geen progressie bij de bench press. Slaap en voeding zijn goed op orde.", question: "Welke aanpassing is voor Lena het meest logisch als volgende stap?", options: ["Direct overstappen naar een 6-daags PPL-schema voor meer volume", "Een deload-week plannen gevolgd door een nieuwe prikkel (van 3×8 naar 5×5 of microload toevoegen)", "Stoppen met bench press en overstappen op dumbbell-isolatieoefeningen", "Trainingsfrequentie halven naar 2x/week voor meer herstel"], correct: 1 }
    ]
  },

  // ══════════════════════════════════════════════════════
  // MODULE 4 — COACHING
  // ══════════════════════════════════════════════════════
  {
    id: 4,
    title: "Coaching",
    subtitle: "Professioneel werken met klanten",
    color: "#907525",
    lessons: [
      {
        id: 12,
        title: "Klantgesprekken",
        duration: "9 min",
        content: `
<h2>Klantgesprekken</h2>
<p>Wanneer iemand zich meldt bij een trainer, begint het proces niet in de sportschool, maar in een gesprek. Veel beginners maken de fout om direct een schema te willen geven, zonder te begrijpen met wie ze werken.</p>

<h3>Wat is een intake?</h3>
<p>Een intake is het eerste moment waarop je inzicht krijgt in de persoon. Niet alleen in <em>wat</em> iemand wil bereiken, maar ook in <em>wat</em> iemand aankan en wie iemand is. Het verschil is cruciaal.</p>

<h3>Essentiële intakevragen</h3>
<ul>
  <li>Wat is het trainingsdoel? (sterker, afvallen, spiermassa, conditie)</li>
  <li>Wat is de trainingsgeschiedenis? (beginner, gevorderd, eerder gestopt)</li>
  <li>Hoe is het herstel? (slaap, voeding, stressniveau)</li>
  <li>Hoe ziet het dagelijks leven eruit? (drukke baan, gezin, reistijd)</li>
  <li>Zijn er eerdere blessures of klachten?</li>
  <li>Hoeveel tijd is er beschikbaar voor training?</li>
  <li>Wat heeft wel en niet gewerkt in het verleden?</li>
</ul>

<h3>Van doel naar schema</h3>
<p>Na de intake vertaal je wat je hebt gehoord naar een concreet plan. Per doel liggen de accenten anders:</p>
<ul>
  <li><strong>Vetverlies:</strong> Nadruk op consistentie en energieverbruik. Het schema moet haalbaar zijn en volgehouden worden.</li>
  <li><strong>Spieropbouw:</strong> Voldoende volume en spierspanning, gecombineerd met goed herstel.</li>
  <li><strong>Krachtontwikkeling:</strong> Hoge intensiteit, langere rusttijden, compound oefeningen als basis.</li>
</ul>

<h3>Verwachtingen managen</h3>
<p>De intake is ook het moment om realistische verwachtingen te scheppen. Hier leg je de basis voor vertrouwen en een haalbare aanpak. Iemand die verwacht in 4 weken 10 kilo af te vallen, heeft een eerlijk gesprek nodig over wat realistisch is.</p>

<div class="lesson-example">
  <strong>Praktijkvoorbeeld:</strong> Een klant geeft aan vier keer per week te willen trainen, maar heeft een drukke baan en slaapt slecht. Op papier lijkt het plan goed, maar in de praktijk is de kans groot dat het niet vol te houden is. De intake helpt dit vroegtijdig te signaleren.
</div>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Een klantgesprek = inzicht in doel, belastbaarheid én de persoon zelf. Dit is de basis voor alles wat volgt.
</div>
        `
      },
      {
        id: 13,
        title: "Motiveren",
        duration: "9 min",
        content: `
<h2>Motiveren</h2>
<p>Een schema maken is één stap, maar het echte coachingwerk begint daarna. Training is een langdurig proces. Motivatie fluctueert — en een goede trainer weet daarmee om te gaan.</p>

<h3>Intrinsieke vs. extrinsieke motivatie</h3>
<p><strong>Extrinsieke motivatie</strong> komt van buitenaf: goedkeuring, complimenten, uiterlijk. Dit kan een goede start zijn, maar is op lange termijn fragiel.</p>
<p><strong>Intrinsieke motivatie</strong> komt van binnenuit: plezier in bewegen, trots op groei, energie voelen. Dit is de duurzamere drijfveer. Als trainer stuur je richting intrinsieke motivatie door de klant bewust te maken van zijn eigen vooruitgang.</p>

<h3>Monitoren en aanpassen</h3>
<p>Monitoren betekent systematisch bijhouden wat er gebeurt:</p>
<ul>
  <li>Wordt iemand sterker? Verbetert de techniek?</li>
  <li>Herstelt hij goed tussen trainingen?</li>
  <li>Is er sprake van overmatige vermoeidheid of blessures?</li>
  <li>Is de motivatie stabiel?</li>
</ul>
<p>Wanneer progressie stagneert — het <strong>plateau</strong> — is dit een moment om in te grijpen. Het lichaam is gewend geraakt aan de belasting en reageert niet meer. Mogelijke oorzaken: belasting niet meer hoog genoeg, te weinig volume, te veel vermoeidheid, voeding of slaap onvoldoende.</p>

<h3>Kleine successen zichtbaar maken</h3>
<p>Motivatie is direct gekoppeld aan het ervaren van vooruitgang. Door kleine successen te benoemen — meer gewicht, betere techniek, meer energie — blijft een klant betrokken en gemotiveerd.</p>

<div class="lesson-example">
  <strong>Praktijkvoorbeeld:</strong> Een klant die na 6 weken "niets ziet". Als trainer wijs je op wat er wél is verbeterd: de squat is 10 kilo zwaarder, de techniek is strakker, de klant slaapt beter. Resultaat is breder dan alleen het uiterlijk.
</div>

<h3>SMART doelen stellen</h3>
<p>Doelen die motivatie vasthouden zijn <strong>SMART</strong>:</p>
<ul>
  <li><strong>Specifiek:</strong> "Ik wil de squat van 80 naar 100 kg brengen"</li>
  <li><strong>Meetbaar:</strong> Gewicht, reps, lichaamssamenstelling</li>
  <li><strong>Acceptabel:</strong> Haalbaar voor deze persoon</li>
  <li><strong>Realistisch:</strong> Passend bij niveau en tijdsbestek</li>
  <li><strong>Tijdgebonden:</strong> "Over 12 weken"</li>
</ul>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Motivatie onderhoud je door voortgang zichtbaar te maken, intrinsieke drijfveren aan te spreken en doelen concreet en haalbaar te houden.
</div>
        `
      },
      {
        id: 14,
        title: "Grenzen aangeven",
        duration: "8 min",
        content: `
<h2>Grenzen aangeven</h2>
<p>In de praktijk heeft bijna elke klant te maken met beperkingen: oude blessures, pijnklachten, bewegingsbeperkingen of te hoge verwachtingen. Hier ligt een belangrijke professionele verantwoordelijkheid.</p>

<h3>Grenzen van de klant respecteren</h3>
<p>Het doel van training is vooruitgang, niet schade. Wanneer een oefening pijn veroorzaakt, is dit een signaal dat er iets niet klopt. Dit betekent niet dat iemand niet kan trainen, maar dat er <strong>aangepast</strong> moet worden.</p>
<p><strong>Denk niet in beperkingen, maar in mogelijkheden.</strong></p>

<div class="lesson-example">
  <strong>Voorbeeld:</strong> Een klant kan geen squat uitvoeren door knieklachten. Dit betekent niet dat hij zijn benen niet kan trainen. Zoek een alternatief: leg press, split squat, step-up of hip thrust — afhankelijk van wat de belastbaarheid toelaat.
</div>

<h3>Individualisering</h3>
<p>Geen enkele klant is hetzelfde. Twee mensen met hetzelfde doel kunnen totaal anders reageren op dezelfde training. De één herstelt snel en kan vaker trainen, terwijl de ander meer rust nodig heeft.</p>
<p><strong>Een trainer moet continu observeren en aanpassen.</strong> Het schema is niet leidend — de persoon is dat.</p>

<h3>Grenzen van de trainer</h3>
<p>Een even belangrijke kant: als trainer heb je ook <strong>eigen professionele grenzen</strong>. Je bent geen arts of fysiotherapeut. Weten wanneer je een klant moet doorverwijzen is een teken van professionaliteit, niet van zwakte.</p>
<p>Situaties waarin doorverwijzen verstandig is:</p>
<ul>
  <li>Aanhoudende pijn ondanks aanpassingen</li>
  <li>Klachten die medische diagnose vereisen</li>
  <li>Psychische problematiek (eetstoornissen, burn-out)</li>
  <li>Letsel dat buiten jouw expertise valt</li>
</ul>

<h3>Gevolgen van signalen negeren</h3>
<p>Pijnsignalen negeren kan leiden tot verergering van klachten en langdurige uitval. Door tijdig aan te passen, kan training juist bijdragen aan herstel en verbetering. Een klant die zich veilig voelt om aan te geven wat hij ervaart, is een klant die langdurig bij je blijft.</p>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Grenzen aangeven geldt voor de klant (beperkingen respecteren) én voor jouzelf (weten wanneer je doorverwijst). Beiden zijn essentieel voor professioneel coachen.
</div>
        `
      },
      {
        id: 15,
        title: "Omgaan met weerstand",
        duration: "9 min",
        content: `
<h2>Omgaan met weerstand</h2>
<p>In de coachingpraktijk loop je vroeg of laat tegen weerstand aan. Een klant die niet komt opdagen, excuses maakt, het schema niet volgt of twijfelt aan zijn eigen kunnen. Weerstand is normaal — het is geen persoonlijk falen van de trainer, maar informatie over de klant.</p>

<h3>Wat is weerstand?</h3>
<p>Weerstand is elke vorm van verzet tegen verandering. Gedragsverandering — en dat is wat training vraagt — is moeilijk. Het vraagt energie, discipline en soms een fundamentele aanpassing van gewoonten. Het is dus logisch dat mensen soms terugdeinzen.</p>

<h3>Veelvoorkomende vormen van weerstand</h3>
<ul>
  <li><em>"Ik heb geen tijd."</em> — Prioriteitsprobleem, of schema is niet realistisch.</li>
  <li><em>"Het is te zwaar."</em> — Belasting klopt niet, of de klant mist vertrouwen.</li>
  <li><em>"Ik zie geen resultaat."</em> — Verwachtingen kloppen niet, of progressie wordt niet gezien.</li>
  <li><em>"Ik voel me schuldig als ik een training mis."</em> — Ongezonde mindset rondom training.</li>
  <li><em>Niet komen zonder bericht.</em> — Vermijdingsgedrag, afstand nemen van de uitdaging.</li>
</ul>

<h3>Hoe ga je ermee om?</h3>
<div class="lesson-system">
  <h4>1. Luister zonder oordeel</h4>
  <p>Weerstand benoemen zonder het weg te wuiven. "Ik hoor dat je het moeilijk vindt om consistent te trainen. Vertel me meer." Dit creëert veiligheid en inzicht.</p>
</div>
<div class="lesson-system">
  <h4>2. Stel open vragen</h4>
  <p>Vragen die de klant laten nadenken: "Wat maakt het lastig voor je?" of "Wat zou het voor jou betekenen als dit wél zou lukken?" Dit activeert zelfmotivatie.</p>
</div>
<div class="lesson-system">
  <h4>3. Rol met de weerstand mee</h4>
  <p>Ga niet tegenin — dat versterkt de weerstand. Erken de uitdaging, maar herformuleer: "Het is inderdaad pittig in het begin. Wat zou een kleine, haalbare stap zijn voor de komende week?"</p>
</div>
<div class="lesson-system">
  <h4>4. Verlaag de drempel</h4>
  <p>Als weerstand groot is, is het schema waarschijnlijk te zwaar of te intensief voor de huidige situatie. Pas aan: minder dagen, kortere sessies, eenvoudigere oefeningen. Kleine successen bouwen zelfvertrouwen op.</p>
</div>

<h3>Veranderbereidheid</h3>
<p>Mensen veranderen niet allemaal op hetzelfde moment. Iemand die "nog niet klaar" is, heeft een andere aanpak nodig dan iemand die vol enthousiasme is. Als trainer lees je waar iemand staat en pas je je stijl aan.</p>

<div class="lesson-example">
  <strong>Gouden regel:</strong> Weerstand is geen obstakel — het is de plek waar het coachingswerk echt begint. Een trainer die weerstand herkent en er professioneel mee omgaat, bouwt een sterkere, duurzamere relatie met zijn klant.
</div>

<div class="lesson-key">
  <strong>Kernbegrip:</strong> Omgaan met weerstand vraagt luisteren, open vragen stellen en de drempel verlagen — niet forceren of negeren.
</div>
        `
      }
    ],
    quiz: [
      {
        question: "Wat is het primaire doel van een intakegesprek bij een nieuwe klant?",
        options: ["Direct een trainingsschema samenstellen", "De maximale kracht van de klant direct testen", "Inzicht in doel, belastbaarheid en persoonlijke situatie", "Kennismaken met de beschikbare apparatuur"],
        correct: 2
      },
      {
        question: "Wat is een 'plateau' in trainingsterminologie?",
        options: ["Periode van bijzonder snelle progressie", "Stagnerende progressie ondanks doorgaande training", "Type periodiseringsschema", "Succesvol afgeronde trainingscyclus"],
        correct: 1
      },
      {
        question: "Wanneer is het het meest aangewezen om een trainingsschema aan te passen?",
        options: ["Nooit — consistentie is alles", "Wekelijks voor maximale variatie", "Uitsluitend aan het begin van een nieuw seizoen", "Bij uitblijven van progressie of veranderde omstandigheden"],
        correct: 3
      },
      {
        question: "Hoe ga je als trainer om met een klant die pijn ervaart bij een specifieke oefening?",
        options: ["De klant aanmoedigen door de pijn te werken", "Stoppen met alle trainingsactiviteiten", "Oefening aanpassen of een veilig alternatief zoeken", "Het gewicht verhogen om de spier te versterken"],
        correct: 2
      },
      {
        question: "Wat is het verschil tussen intrinsieke en extrinsieke motivatie?",
        options: ["Extrinsiek is duurzamer dan intrinsiek", "Intrinsiek komt van binnenuit (eigen plezier, groei); extrinsiek van buitenaf (goedkeuring, uiterlijk)", "Er is geen praktisch verschil in trainingscontext", "Intrinsieke motivatie geldt alleen voor gevorderde sporters"],
        correct: 1
      },
      {
        question: "Wat is een veelgemaakte fout bij beginners die snel resultaat willen?",
        options: ["Te weinig trainen door angst voor overbelasting", "Te licht gewicht gebruiken voor alle oefeningen", "Te veel te snel doen waardoor blessures en overtraining ontstaan", "Te gezond eten waardoor herstel te snel verloopt"],
        correct: 2
      },
      {
        question: "In welke situatie is het verstandig een klant door te verwijzen naar een arts of fysiotherapeut?",
        options: ["Als de klant zijn schema wil aanpassen na twee weken", "Bij aanhoudende pijn of medische klachten buiten jouw expertise", "Als een klant geen progressie maakt na twee weken", "Altijd voor de start van elk nieuw trainingsprogramma"],
        correct: 1
      },
      {
        question: "Wat is de beste reactie als een klant weerstand toont tegen het trainingsschema?",
        options: ["Het schema streng vasthouden — consistentie gaat boven alles", "De klant verplichten het schema te volgen voor zijn eigen bestwil", "Luisteren zonder oordeel, open vragen stellen en de drempel eventueel verlagen", "Het schema volledig herschrijven zonder verdere gesprekken"],
        correct: 2
      },
      {
        question: "Hoe motiveer je een klant het meest effectief op lange termijn?",
        options: ["Constante druk door hoge verwachtingen te stellen", "Benadrukken wat nog niet goed gaat", "Kleine successen zichtbaar maken en positief begeleiden", "Schema regelmatig verzwaren"],
        correct: 2
      },
      {
        type: "casus",
        casus: "Sophie (38) meldt zich bij jou. Ze wil afvallen voor haar bruiloft over 4 maanden. Ze werkt fulltime, heeft twee jonge kinderen en kan maximaal 2x per week naar de sportschool. Bij de intake geeft ze aan lichte knieklachten te hebben bij squatten. Na 3 weken zegt ze: 'Ik zie het niet meer zitten, ik kom nooit op tijd.'",
        question: "Wat is de meest complete en professionele aanpak voor Sophie op dit moment?",
        options: ["Direct een intensief dagprogramma starten voor maximale resultaten", "Alle beentraining vermijden vanwege de knieklachten", "Luisteren naar haar weerstand, het schema realistisch aanpassen naar 2x/week met squat-alternatieven, en kleine successen benoemen om motivatie te herstellen", "Sophie adviseren meer dan 2x te trainen voor haar doel"],
        correct: 2
      },
      { question: "Wat omschrijft actief luisteren het best in een coachingsgesprek?", options: ["Alleen knikken en de klant laten uitpraten zonder interruptie", "Volledig aanwezig zijn, parafraseren wat je hoort en doorvragen op zowel inhoud als gevoel", "Aantekeningen maken van alle feiten die de klant noemt", "Direct een oplossing aanbieden zodra de klant klaar is met praten"], correct: 1 },
      { question: "Welke techniek helpt het meest om een klant zijn eigen motivatie te laten ontdekken?", options: ["Gesloten vragen die snelle antwoorden uitlokken", "Directe instructies geven die duidelijkheid scheppen", "Open vragen stellen die de klant aanzetten tot zelfreflectie", "Uitsluitend complimenten geven om vertrouwen op te bouwen"], correct: 2 },
      { question: "Wat is het GROW-model in coaching?", options: ["Een voedingsplan gekoppeld aan trainingsdoelen", "Een coachingsraamwerk (Goal, Reality, Options, Will) dat gesprek structureert van doel naar actieplan", "Een periodiseringsmodel voor gevorderde sporters", "Een beoordelingsschaal voor trainingstechniek"], correct: 1 },
      { question: "Wanneer is empathie tonen als trainer het meest essentieel?", options: ["Nooit — professionele afstand is altijd vereist", "Alleen bij het eerste intakegesprek", "Als een klant weerstand toont, frustratie ervaart of geconfronteerd wordt met tegenslagen", "Uitsluitend bij klanten met psychologische problematiek"], correct: 2 },
      { question: "Hoe evalueer je de voortgang van een klant het meest volledig?", options: ["Wekelijks wegen als enige maatstaf", "Objectieve maten (kracht, lichaamssamenstelling) combineren met subjectieve feedback (energie, motivatie, slaap)", "Uitsluitend voor-en-nafoto's vergelijken", "Alleen sportschoolprestaties bijhouden"], correct: 1 },
      { question: "Wat is gymschroom en wat is de beste aanpak als trainer?", options: ["Een tijdelijk probleem dat vanzelf verdwijnt — geen actie nodig", "Angst voor beoordeling in de sportschool; vertrouwen opbouwen via beheersbare ervaringen en stap-voor-stap progressie", "Een medisch probleem — altijd doorverwijzen naar een psycholoog", "Normaal gedrag dat je aanpakt door de klant direct in het diepe te gooien"], correct: 1 },
      { question: "Een klant haalt zijn schema herhaaldelijk niet en geeft steeds nieuwe excuses. Wat is de meest effectieve coachingsreactie?", options: ["Schema streng vasthouden en de klant aanspreken op zijn discipline", "Doelen verhogen zodat de klant meer gemotiveerd raakt door de uitdaging", "De situatie onderzoeken, de drempel verlagen en kleine haalbare tussenstappen definiëren", "De klant volledig vrijlaten om zelf verantwoordelijkheid te nemen"], correct: 2 },
      { type: "casus", casus: "Rachid (30) is 4 weken in zijn traject. Hij ziet objectieve progressie maar zegt dat training 'niet leuk meer is' en overweegt te stoppen. Hij traint 4x/week intensief.", question: "Wat is als trainer jouw eerste prioriteit in dit gesprek?", options: ["Rachid verplichten door te gaan omdat hij objectief progressie maakt", "Direct het schema verlagen naar 1x/week om weerstand weg te nemen", "Actief luisteren om de onderliggende reden te achterhalen en intrinsieke motivatie activeren door kleine successen te benoemen", "Rachid doorverwijzen naar een sportpsycholoog"], correct: 2 }
    ]
  }
];

export const finalExam = []; // Eindtoets staat in examData.js
